import { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, MenuItem, Select,
  FormControl, InputLabel, Autocomplete, CircularProgress,
  FormControlLabel, IconButton, Checkbox, useMediaQuery, useTheme,
  Stepper, Step, StepLabel, Switch, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AuthContext } from '../context/AuthProvider';
import BrowserTitle from '../components/Helmet';
import propertiesServices from '../services/propertiesServices';
import { stringifyAddress } from '../utils/formatter';
import { calculateTotalPay } from '../utils/proposalCalculations';
import WarrantyTicketFormSections from '../components/WarrantyTicketFormSections';
import useWarrantyTicketForm, { STEP_LABELS } from '../hooks/useWarrantyTicketForm';

export default function WarrantyTicketCreate() {
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const preselectedPropertyId = searchParams.get('propertyId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const ctl = useWarrantyTicketForm({
    userData,
    onSuccess: () => navigate('/warranty-tickets'),
  });

  const {
    isAdminLike, expertMode, setExpertMode, currentStep, goNextStep, goPrevStep,
    selectedProperty, proposals, legacyProposals, notAirmaxxInstallation, clientInfo,
    ccProjectUrl, proposalSelectValue, orderType, form, errors, submitting, showReview,
    selectProperty, handleProposalSelect, handleNotAirmaxxToggle, handleFormChange, handleSubmit,
  } = ctl;

  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (preselectedPropertyId) {
      propertiesServices.getPropertyById(preselectedPropertyId).then(res => {
        if (res.status === 200) selectProperty(res.data);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedPropertyId]);

  const handleSearch = useCallback((query) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await propertiesServices.searchRequest(0, 30, query.trim(), false);
        if (response.status === 200 || response.status === 206) {
          setSearchResults(response.data?.data || []);
        }
      } catch (e) {
        setSearchResults([]);
      }
      setSearchLoading(false);
    }, 300);
  }, []);

  const addressSlot = (expertMode || currentStep === 1) && (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>Address & Customer</Typography>
        {ccProjectUrl && (
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} component="a" href={ccProjectUrl} target="_blank" rel="noopener noreferrer">
            Open CompanyCam
          </Button>
        )}
      </Box>
      <Autocomplete
        freeSolo
        options={searchResults}
        getOptionLabel={(option) => typeof option === 'string' ? option : stringifyAddress(option)}
        loading={searchLoading}
        onInputChange={(_, value) => { handleSearch(value); }}
        onChange={(_, value) => selectProperty(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Address *"
            error={!!errors.property}
            helperText={errors.property}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchLoading && <CircularProgress size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 2 }}
      />

      {selectedProperty && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{stringifyAddress(selectedProperty)}</Typography>
          {clientInfo && (
            <Typography variant="body2" color="text.secondary">
              Customer: {clientInfo.firstName} {clientInfo.lastName}
              {clientInfo.phoneNumber && ` • ${clientInfo.phoneNumber}`}
            </Typography>
          )}
        </Box>
      )}

      {selectedProperty && (proposals.length > 0 || legacyProposals.length > 0) && (
        <FormControl fullWidth error={!!errors.proposal} disabled={notAirmaxxInstallation}>
          <InputLabel>Select Proposal *</InputLabel>
          <Select value={proposalSelectValue} label="Select Proposal *" onChange={(e) => handleProposalSelect(e.target.value)}>
            {proposals.map((p) => {
              const amount = calculateTotalPay(p);
              return (
                <MenuItem key={p.id} value={p.id}>
                  {p.publicId || p.id?.slice(0, 8)} — {p.status}{p.proposalType && ` (${p.proposalType})`}
                  {amount > 0 && ` — ${amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                </MenuItem>
              );
            })}
            {legacyProposals.length > 0 && proposals.length > 0 && (
              <MenuItem disabled divider sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>— Legacy Proposals —</MenuItem>
            )}
            {legacyProposals.map((lp) => (
              <MenuItem key={`legacy:${lp.proposalNumber}`} value={`legacy:${lp.proposalNumber}`}>
                {lp.proposalNumber} — {lp.status} (Legacy)
                {Number(lp.price) > 0 && ` — ${Number(lp.price).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
              </MenuItem>
            ))}
          </Select>
          {errors.proposal && (
            <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>{errors.proposal}</Typography>
          )}
        </FormControl>
      )}

      {selectedProperty && proposals.length === 0 && legacyProposals.length === 0 && !notAirmaxxInstallation && (
        <Typography color="text.secondary">No completed/pending proposals found for this address</Typography>
      )}

      {selectedProperty && (
        <FormControlLabel
          control={<Checkbox checked={notAirmaxxInstallation} onChange={(e) => handleNotAirmaxxToggle(e.target.checked)} />}
          label="Not Airmaxx Installation (no PO)"
          sx={{ mt: 1 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <TextField label="Customer Phone" value={form.customerPhone} onChange={(e) => handleFormChange('customerPhone', e.target.value)} fullWidth />
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <BrowserTitle title="Create Warranty Ticket" />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>New Warranty Ticket</Typography>
        {isAdminLike && (
          <Tooltip title={expertMode ? 'Switch to step-by-step wizard' : 'Show the full form at once'}>
            <FormControlLabel
              control={<Switch size="small" checked={expertMode} onChange={(e) => setExpertMode(e.target.checked)} />}
              label={<Typography variant="caption">Expert mode</Typography>}
              sx={{ m: 0 }}
            />
          </Tooltip>
        )}
      </Box>

      {!expertMode && (
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel={!isMobile}>
            {STEP_LABELS.map((label, i) => (
              <Step key={label} completed={i < currentStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <WarrantyTicketFormSections ctl={ctl} isMobile={isMobile} addressSlot={addressSlot} />

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>Cancel</Button>
        {!expertMode && currentStep > 0 && (
          <Button variant="outlined" onClick={goPrevStep} disabled={submitting}>Back</Button>
        )}
        {!expertMode && currentStep < STEP_LABELS.length - 1 ? (
          <Button
            variant="contained"
            onClick={goNextStep}
            disabled={submitting || (currentStep === 0 && !orderType)}
            size="large"
            sx={{ minWidth: 140 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color={showReview ? 'success' : 'primary'}
            onClick={handleSubmit}
            disabled={submitting}
            size="large"
            startIcon={showReview ? (submitting ? null : <SendIcon />) : null}
            sx={{ minWidth: 200 }}
          >
            {submitting ? <CircularProgress size={24} /> : showReview ? 'Create Ticket & Send Order' : 'Create Ticket'}
          </Button>
        )}
      </Box>
    </Box>
  );
}
